import { msg } from "@omegagrid/localize";
import { ColorTypes, getThemeManager } from "@omegagrid/core";
import { CellModel, CellRange, ColumnSource, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions, formatBytes } from "@reporting/rp-app-host";
import { DbFile } from "@reporting/rp-types/server";
import { databaseKey } from "./serverTreeAdapter";

export class DatabaseGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _databarRanges: CellRange[] = [];

	get activeThemeType() { return getThemeManager().activeTheme.type }

	constructor(
		private idServer?: number
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			w: 5 + 20 * 1,
			content: [{
				action: 'tables',
				title: msg('Tables'),
				icon: 'table-list',
				color: ColorTypes.Accent,
			}],
			filter: false,
			sorting: false,
		}, {
			data: '_name',
			header: `${msg('Database')} / ${msg('File')}`,
			t: 't',
			w: 600,
			levelIndent: true,
		}, {
			data: '_size',
			header: msg('Size'),
			t: 'n',
			w: 300,
			filter: false,
		}, {
			data: 'db_name',
			t: 't',
			hidden: true,
		}, {
			data: 'id_server',
			t: 'n',
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		const rows: RowSource[] = [];
		const dbFiles = await rp.ms('server').get<DbFile[]>(
			`dbFiles/${this.idServer}`,
			{ fields: ['db_name', 'id_server', 'physical_name', 'file_size', 'size', 'accountings']}
		);
		let prev: DbFile | undefined;
		for (const dbFile of dbFiles) {
			if (!prev || prev.db_name !== dbFile.db_name) {
				rows.push({ l: 0, data: {
					...dbFile,
					_name: { t: 'h', v: `<b>${dbFile.db_name}</b> <i style="color: GoldenRod;">(${dbFile.accountings})</i>` },
					_size: { dv: formatBytes(dbFile.size), v: dbFile.size },
				}});
			}
			rows.push({ l: 1, data: {
				...dbFile,
				controls: { content: [] },
				_name: dbFile.physical_name,
				_size: { dv: formatBytes(dbFile.file_size), v: dbFile.file_size },
			}});
			const cr = new CellRange(2, rows.length - 1, 2, rows.length - 1);
			this._databarRanges.push(cr);
			prev = dbFile;
		}
		return rows;
	}

	afterLoad() {
		if (this.editor.grid.model.rows.length) {
			this.editor.grid.model.conditionalFormats.formats.push({
				type: 'bar',
				ranges: this._databarRanges,
				color: this.activeThemeType === 'dark' ? '981f1fff' : 'f77171ff',
			});
			this.editor.grid.model.clearCalculatedStyles();
		}
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: null,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			rowTree: true,
			toolbar: {
				autoUpdate: { enabled: true },
				save: { enabled: false },
				discard: { enabled: false },
				new: { enabled: false },
			},
		});
	}

	onAction(action: string, cell: CellModel): void {
		if (action === 'tables') {
			rp.host.open(
				`super-tables-${databaseKey(cell?.row.data.id_server, cell?.row.data.db_name)}`,
				{ title: `${msg('Tables')}: ${cell?.row.data.db_name}` }
			);
		}
	}

}
